// auth.ts — wraps window.auth and Firebase boot. Exposes a tiny
// observable so the header (and any page) can react when sign-in /
// sign-out happens.
//
// auth state derives from window.auth (single source of truth, set by
// vendor/api.js). We subscribe to window.auth's change listener at boot
// so any mutation — via loginWith*, logout, or a manual window.auth.set
// from the console — propagates here automatically.

import type { BFFUser } from './types';
import { roleToUiRole } from './util';

type Listener = (u: BFFUser | null) => void;
const listeners = new Set<Listener>();

let currentUser: BFFUser | null = null;
let bootChecked = false;

function notify(): void {
  for (const fn of listeners) {
    try { fn(currentUser); } catch (e) { console.error(e); }
  }
}

function syncFromWindowAuth(): void {
  const stored = window.auth.get();
  const next = (stored?.user || null) as BFFUser | null;
  // Only fire listeners when the user actually changed.
  if (next !== currentUser) {
    currentUser = next;
    notify();
  }
}

export function getUser(): BFFUser | null { return currentUser; }
export function getUserRole(): 'public' | 'user' | 'approver' | 'admin' {
  return roleToUiRole(currentUser?.Role || (currentUser as { role?: string } | null)?.role);
}
export function isBootChecked(): boolean { return bootChecked; }

export function onUserChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Wait for Firebase, then re-validate the stored auth via /api/me.
 *  Mirrors frontend2/App.jsx boot sequence exactly. */
export async function bootAuth(): Promise<void> {
  // Subscribe to window.auth so any mutation (login*, logout, or a
  // manual window.auth.set from the console) propagates into our
  // listeners. The subscriber returns an unsubscribe — we never call
  // it because the auth module lives for the whole page lifetime.
  window.auth.subscribe(() => { syncFromWindowAuth(); });

  // Seed from localStorage synchronously so the header gets the cached
  // user on first paint.
  syncFromWindowAuth();

  try {
    const firebaseUser = await window.firebaseAuth.ready();
    if (firebaseUser) {
      try {
        const u = await window.api.me();
        window.auth.set({ token: '__firebase__', user: u });
      } catch {
        await window.firebaseAuth.signOut().catch(() => {});
        window.auth.logout();
      }
    } else {
      const s = window.auth.get();
      if (s?.token && s.token !== '__firebase__') {
        try {
          const u = await window.api.me();
          window.auth.set({ token: s.token, user: u });
        } catch {
          window.auth.logout();
        }
      }
    }
  } finally {
    bootChecked = true;
    notify();
  }
}

export async function logout(): Promise<void> {
  await window.firebaseAuth.signOut().catch(() => {});
  window.auth.logout();
  // window.auth.subscribe will fire and sync us.
}

/** Used by Login/Signup pages. window.auth's listener does the propagation. */
export async function loginWithGoogle(): Promise<BFFUser> {
  return window.auth.loginWithGoogle();
}
export async function loginWithEmail(email: string): Promise<BFFUser> {
  return window.auth.loginWithEmail(email);
}
export async function loginWithToken(token: string): Promise<BFFUser> {
  return window.auth.loginWithToken(token);
}
